// page-backlog-review.ts — R3 : la revue (constat + arbitrage), vue Liste.
//
// Un seul écran, deux zones : le constat en tête (repliable), la liste des
// recommandations en dessous. Pas d'onglets — c'est la même liste qui s'enrichit
// et se vide, jamais un espace de travail parallèle.
//
// Trois règles de cet écran, toutes issues du cadrage :
//
// 1. **L'ordre ne change jamais après une action.** La ligne traitée change
//    d'aspect SUR PLACE ; réordonner ferait perdre le fil au PMO.
// 2. **Le système signale, il ne rejuge pas.** Les faits qui ont bougé sont
//    soulignés ; aucune recommandation n'est requalifiée toute seule.
// 3. **Le garde-fou se déclenche à l'APPLICATION**, une seule fois, au seul
//    moment où une décision devient irréversible.
//
// La vue Tableau (`page-backlog-review-board.ts`) double la vue Liste au
// glisser-déposer, sans jamais la remplacer : la vue Liste reste la référence
// fonctionnelle et le seul chemin garanti au clavier.
//
// Découpé le 2026-09-13 : le constat et le cadrage (`-constat`), la liste et
// ses filtres (`-list`), les gestes d'arbitrage (`-gestures`), les menus de
// l'entête (`-menus`) et les types (`-types`) vivent à côté. Ici ne restent
// que l'état de l'écran, le suivi d'une passe et le câblage.

import { filterRecommendations, type SortKey } from '../domain/backlog-review.js';
import { definePage, navigateTo } from '../router.js';
import { bindBoard } from './page-backlog-review-board.js';
import { openCommitteeFeedbackModal } from './page-backlog-review-committee.js';
import {
  downloadDecisions,
  hasApplied,
  hasFlagged,
  openArbitrationModal,
  openOutcomeModal,
} from './page-backlog-review-aval.js';
import {
  openVitalityHelp,
  renderAnomalies,
  renderConstat,
  renderDrift,
  renderParams,
} from './page-backlog-review-constat.js';
import { openDossierModal } from './page-backlog-review-dossier.js';
import {
  askAnnotation,
  boardHandlers,
  patchReco,
  requestPile,
  type GestureContext,
} from './page-backlog-review-gestures.js';
import { openImportModal } from './page-backlog-review-import.js';
import { renderRecos, type ListState } from './page-backlog-review-list.js';
import { bindMenus, menuHtml, type MenuItem } from './page-backlog-review-menus.js';
import { freezeControls, runBanner, runError, runModalBody } from './page-backlog-review-run.js';
import { label, type Review, type Run } from './page-backlog-review-types.js';
import { renderReviewCreation } from './page-backlog-reviews.js';

let _review: Review | null = null;
let _collapsed = false;
// Les paramètres de création, repliés par défaut : on revient sur une revue
// pour arbitrer, pas pour relire son cadrage.
let _paramsCollapsed = true;
const _list: ListState = {
  pile: 'todo',
  family: '',
  onlyFlagged: false,
  sort: 'reco',
  showAll: false,
  view: 'list',
};
// Les gestes lisent la revue AFFICHÉE au moment où ils s'exécutent, pas celle
// qui l'était quand la modale s'est ouverte.
const _ctx: GestureContext = { review: () => _review, reload: () => _reload() };

function _syncHash(): void {
  const id = _review?.id;
  if (!id) return;
  const params = new URLSearchParams();
  if (_list.pile !== 'todo') params.set('pile', _list.pile);
  if (_list.family) params.set('family', _list.family);
  if (_list.onlyFlagged) params.set('flagged', '1');
  const query = params.toString();
  // Le filtre courant vit dans l'URL : un lien partagé ou un rafraîchissement
  // retrouve la même vue. `replaceState` plutôt que `navigate` — changer de
  // filtre n'est pas une navigation, et ne doit pas remplir l'historique.
  history.replaceState(null, '', `#review/${id}${query ? '?' + query : ''}`);
}

function _readHash(): void {
  const raw = location.hash.split('?')[1] || '';
  const params = new URLSearchParams(raw);
  _list.pile = params.get('pile') || 'todo';
  _list.family = params.get('family') || '';
  _list.onlyFlagged = params.get('flagged') === '1';
}

// ── Itération ────────────────────────────────────────────────────────────────

// Le front interroge le run toutes les trois secondes tant qu'il tourne. La
// charge utile est minimale, exprès : renvoyer la revue entière pour lire un
// compteur serait absurde.
const RUN_POLL_MS = 3000;
let _pollTimer: number | null = null;

// La popup d'attente est OUVERTE par le suivi du run, et refermée par lui. Le
// drapeau évite de la rouvrir à chaque tour de polling — sans lui, chaque
// rafraîchissement remettrait le focus dans la modale et volerait le curseur.
let _runModalOpen = false;
// Le run que l'utilisateur a explicitement FERMÉ. Sans cette mémoire, le tour
// de polling suivant rouvrait la popup qu'on venait de fermer : `_render` appelle
// `_syncRunModal`, qui ne voyait qu'un run en cours et pas de popup ouverte.
// On garde l'identifiant, pas un booléen : une passe suivante doit rouvrir.
let _runModalDismissed: number | null = null;

/** Ouvre, met à jour, ou referme la popup selon l'état du run.
 *
 * Fermable, et c'est voulu : le calcul vit côté serveur, l'utilisateur doit
 * pouvoir reprendre la main sans l'interrompre. Le bouton d'action ANNULE
 * réellement la passe — il ne se contente pas de masquer la fenêtre.
 */
function _syncRunModal(run: Run | null): void {
  if (run === null || run.status !== 'running') {
    // La passe est finie : on oublie le refus, pour que la SUIVANTE s'ouvre.
    _runModalDismissed = null;
    if (_runModalOpen) {
      _runModalOpen = false;
      closeModal();
    }
    return;
  }
  if (_runModalOpen) {
    const body = document.getElementById('modal-body');
    // La popup peut avoir été fermée par la croix, Échap ou un clic dehors :
    // ces trois chemins passent par `closeModalSafe` et ne touchent pas le
    // drapeau, qui reste vrai. Sans vérifier À QUI appartient `#modal-body`, on
    // écrirait la progression du run dans la modale ouverte entre-temps —
    // l'aide du barème de vitalité, par exemple, qui reste cliquable pendant
    // une passe. `btn-run-abort` n'existe que dans le corps de la popup
    // d'attente : c'est lui qui l'identifie.
    if (body?.querySelector('#btn-run-abort')) {
      body.innerHTML = runModalBody(run);
      return;
    }
    // Fermée autrement que par « Fermer ». On aligne le drapeau et on traite la
    // fermeture comme un refus, sinon la popup se rouvrirait au tour suivant.
    _runModalOpen = false;
    _runModalDismissed = run.id;
    return;
  }
  // Fermée à la main : on ne la rouvre pas dans le dos de l'utilisateur. Le
  // bandeau prend le relais, et son bouton la rappelle s'il la veut.
  if (_runModalDismissed === run.id) return;
  _runModalOpen = true;
  const reviewId = _review?.id;
  const runId = run.id;
  // UN SEUL bouton, « Fermer » : le pied portait deux « Annuler » — celui de la
  // modale et celui du run — et rien ne disait lequel arrêtait quoi. Fermer ne
  // fait que fermer ; l'analyse continue côté serveur, c'est tout l'intérêt de
  // la faire tourner là-bas. L'arrêt reste possible, en lien discret dans le
  // corps : c'est un geste rare, il n'a pas à disputer la place au geste normal.
  openModal(
    t('review.run.waitTitle'),
    runModalBody(run),
    () => {
      _runModalOpen = false;
      _runModalDismissed = runId;
      closeModal();
    },
    t('common.close'),
  );
  const footerCancel = document.getElementById('modal-cancel');
  if (footerCancel) footerCancel.style.display = 'none';
  document.getElementById('btn-run-abort')?.addEventListener('click', () => {
    void (async () => {
      _runModalOpen = false;
      closeModal();
      if (reviewId) await api.cancelBacklogRun(reviewId, runId);
      _stopPolling();
      await _reload();
    })();
  });
}

function _stopPolling(): void {
  if (_pollTimer !== null) {
    window.clearTimeout(_pollTimer);
    _pollTimer = null;
  }
}

function _askIterate(): void {
  if (!_review) return;
  // Capturé AVANT la confirmation : elle s'exécute plus tard, et `_review` peut
  // avoir changé si l'utilisateur a navigué entre-temps. On relance la revue
  // qu'il avait sous les yeux, pas celle qui se trouve chargée à cet instant.
  const reviewId = _review.id;
  openModal(
    t('review.iterate.title'),
    `<p>${esc(t('review.iterate.intro'))}</p><p><strong>${esc(t('review.iterate.keeps'))}</strong></p>`,
    async () => {
      closeModal();
      await api.iterateBacklogReview(reviewId);
      await _reload();
      _poll();
    },
    t('review.iterate.confirm'),
  );
}

/** Suit le run sans bloquer l'écran : le constat reste visible, les
 *  recommandations déjà présentes restent manipulables. */
function _poll(): void {
  _stopPolling();
  if (!_review) return;
  _pollTimer = window.setTimeout(() => {
    void (async () => {
      if (!_review) return;
      const body = (await api.getBacklogRun(_review.id)) as { run: Run | null };
      const wasRunning = _review.run?.status === 'running';
      _review.run = body.run;
      if (body.run?.status === 'running') {
        _render();
        _poll();
        return;
      }
      // La passe n'est plus en cours : on RECHARGE, toujours. C'est ce rendu
      // qui rend la main sur les CTA gelés et retire le bandeau — s'en
      // remettre au seul cas `wasRunning` laissait l'écran figé quand l'état
      // précédent n'avait pas été observé (retour sur la revue, run déjà fini
      // au premier tour).
      await _reload();
      if (wasRunning && body.run?.status === 'succeeded') toast(t('review.run.done'));
    })();
  }, RUN_POLL_MS);
}

// ── Câblage ──────────────────────────────────────────────────────────────────

/** Une analyse démarrée reprend le suivi exactement comme une itération
 *  procédurale : même bandeau d'attente, même annulation, même reprise. */
function _openAiIteration(): void {
  if (!_review) return;
  openDossierModal(_review.id, () => {
    void (async () => {
      await _reload();
      _poll();
    })();
  });
}

function _reco(raw: string | undefined) {
  return _review?.recommendations.find((r) => r.id === Number(raw));
}

function _bindHeaderBlocks(): void {
  document.getElementById('btn-iterate')?.addEventListener('click', () => _askIterate());
  document.getElementById('btn-retry-ai')?.addEventListener('click', () => _openAiIteration());
  document.getElementById('btn-collapse')?.addEventListener('click', () => {
    _collapsed = !_collapsed;
    _render();
  });
  document.getElementById('btn-vitality-help')?.addEventListener('click', () => openVitalityHelp());
  document.getElementById('btn-run-reopen')?.addEventListener('click', () => {
    // Rouvre la popup refermée : le bandeau est un rappel, pas un suivi — la
    // progression vit dans la popup. On lève le refus, sinon `_syncRunModal`
    // respecterait la fermeture précédente et ne rouvrirait rien.
    _runModalDismissed = null;
    _runModalOpen = false;
    _syncRunModal(_review?.run ?? null);
  });
  document.getElementById('btn-params-collapse')?.addEventListener('click', () => {
    _paramsCollapsed = !_paramsCollapsed;
    _render();
  });
  // Enregistrement EXPLICITE, comme le bloc d'informations d'un plan d'action :
  // le titre se corrige en plusieurs frappes, et sauver à chaque touche
  // enverrait une requête par lettre. Le plafond part avec, dans le même appel.
  document.getElementById('btn-params-save')?.addEventListener('click', (e) => {
    void (async () => {
      if (!_review) return;
      const title =
        (document.getElementById('param-title') as HTMLInputElement | null)?.value.trim() || '';
      const limit = (document.getElementById('param-limit') as HTMLInputElement | null)?.checked;
      await api.withButton(e.currentTarget as HTMLButtonElement, async () => {
        if (!_review) return;
        try {
          await api.patchBacklogReview(_review.id, {
            title: title || null,
            limit_recommendations: limit !== false,
          });
          _list.showAll = false;
          await _reload();
          toast(t('common.saved'));
        } catch (err: unknown) {
          toast(err instanceof Error ? err.message : t('common.error'), 'error');
        }
      });
    })();
  });
}

function _bindList(): void {
  document.querySelectorAll<HTMLElement>('[data-view]').forEach((btn) => {
    btn.addEventListener('click', () => {
      _list.view = btn.dataset.view === 'board' ? 'board' : 'list';
      _render();
    });
  });
  if (_list.view === 'board' && _review) {
    bindBoard(
      filterRecommendations(_review.recommendations || [], {
        family: _list.family,
        onlyFlagged: _list.onlyFlagged,
      }),
      boardHandlers(_ctx),
    );
  }
  document.getElementById('btn-show-all')?.addEventListener('click', () => {
    _list.showAll = true;
    _render();
  });
  document.getElementById('reco-sort')?.addEventListener('change', (e) => {
    _list.sort = (e.target as HTMLSelectElement).value as SortKey;
    _render();
  });
  document.querySelectorAll<HTMLElement>('[data-pile]').forEach((chip) => {
    chip.addEventListener('click', () => {
      _list.pile = chip.dataset.pile || 'todo';
      _list.showAll = false;
      _syncHash();
      _render();
    });
  });
  document.querySelectorAll<HTMLElement>('[data-family]').forEach((chip) => {
    chip.addEventListener('click', () => {
      _list.family = chip.dataset.family || '';
      _syncHash();
      _render();
    });
  });
  document.querySelectorAll<HTMLElement>('[data-flagged]').forEach((chip) => {
    chip.addEventListener('click', () => {
      _list.onlyFlagged = !_list.onlyFlagged;
      _syncHash();
      _render();
    });
  });
  document.querySelectorAll<HTMLElement>('[data-flag]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const reco = _reco(btn.dataset.flag);
      if (reco) void patchReco(_ctx, reco.id, { to_arbitrate: !reco.to_arbitrate });
    });
  });
  document.querySelectorAll<HTMLElement>('[data-note]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const reco = _reco(btn.dataset.note);
      if (reco) askAnnotation(_ctx, reco);
    });
  });
  document.querySelectorAll<HTMLElement>('[data-act]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const reco = _reco(btn.dataset.id);
      if (reco) requestPile(_ctx, reco, btn.dataset.act || '');
    });
  });
}

function _render(): void {
  if (!_review) return;
  setContent(
    runBanner(_review.run) +
      runError(_review.run) +
      renderAnomalies(_review) +
      renderParams(_review, _paramsCollapsed) +
      renderDrift(_review) +
      renderConstat(_review, _collapsed) +
      renderRecos(_review, _list, boardHandlers(_ctx)),
  );
  _bindHeaderBlocks();
  _bindList();
  // Après le câblage : il pose les écouteurs, on gèle ensuite ce qui ne doit
  // pas être cliqué. L'inverse laisserait les boutons réactivés par le câblage.
  freezeControls(_review.run);
  // Après le câblage aussi : ouvrir la modale avant lui ferait poser les
  // écouteurs de la page sur un DOM que la modale recouvre déjà.
  _syncRunModal(_review.run);
}

async function _reload(): Promise<void> {
  if (!_review) return;
  _review = (await api.getBacklogReview(_review.id)) as Review;
  _render();
}

// ── Entête ───────────────────────────────────────────────────────────────────

function _renderHeader(review: Review): void {
  // « Saisir le retour de comité » n'apparaît qu'une fois un support produit :
  // avant, il n'y a rien eu à présenter, donc rien à rapporter.
  // Toutes les sorties de l'entête sont SECONDAIRES : une seule action y est
  // primaire, « Itérer ». Mélanger les deux styles sur une même rangée ne
  // hiérarchise rien, il fait juste hésiter.
  const feedbackItem = review.supports?.length
    ? { id: 'btn-committee-feedback', label: t('review.committee.open') }
    : null;
  // Les trois sorties d'aval n'apparaissent QUE quand elles ont un sens : le
  // journal dès la première application, la sollicitation dès qu'une position
  // attend une décision, le bilan après la clôture. Un bouton présent mais
  // inopérant est une promesse non tenue.
  const recos = review.recommendations || [];
  const decisionsItem = hasApplied(recos)
    ? { id: 'btn-decisions', label: t('review.aval.decisions') }
    : null;
  const arbitrationItem = hasFlagged(recos)
    ? { id: 'btn-arbitration', label: t('review.aval.arbitration') }
    : null;
  const outcomeItem =
    review.status === 'closed' ? { id: 'btn-outcome', label: t('review.aval.outcome') } : null;
  const keep = (items: Array<MenuItem | null>): MenuItem[] => items.filter(Boolean) as MenuItem[];
  setHeader(
    // Le titre s'il existe, le périmètre sinon : une revue d'avant le
    // 2026-08-26 n'en a pas, et lui en inventer un serait mentir.
    t('review.page.title', { scope: review.title || review.label }),
    // Trois menus, par intention : analyser, présenter, solliciter. « Kit »
    // disparaît en tant que CTA — c'est devenu « Itération IA », parce que
    // c'est ce qu'il fait : une passe de détection, confiée à l'IA.
    `${menuHtml(
      'btn-present',
      t('review.menu.present'),
      keep([{ id: 'btn-support', label: t('review.support.compose') }, decisionsItem, outcomeItem]),
    )}
     ${menuHtml('btn-ask', t('review.menu.ask'), keep([arbitrationItem, feedbackItem]))}
     ${menuHtml(
       'btn-iterate-menu',
       t('review.iterate.button'),
       [
         { id: 'btn-iterate-procedural', label: t('review.iterate.procedural') },
         { id: 'btn-iterate-ai', label: t('review.iterate.ai') },
         // Le retour du kit est une passe comme les deux autres : elle produit
         // des recommandations sur le même périmètre, par le même chemin
         // d'écriture. Sa place est ici, pas dans la modale de ce qui SORT.
         { id: 'btn-import-kit', label: t('review.import.menu') },
       ],
       true,
     )}`,
  );
  bindMenus(['btn-present', 'btn-ask', 'btn-iterate-menu']);
  document.getElementById('btn-support')?.addEventListener('click', () => {
    if (_review) navigateTo('review/:id/support', { id: _review.id });
  });
  document.getElementById('btn-decisions')?.addEventListener('click', (e) => {
    if (_review) void downloadDecisions(_review.id, e.currentTarget as HTMLButtonElement);
  });
  document.getElementById('btn-arbitration')?.addEventListener('click', () => {
    if (!_review) return;
    openArbitrationModal(_review.id, () => void _reload(), {
      flagged: _review.recommendations.filter((r) => r.to_arbitrate),
      familyLabel: (f: string) => label('review.family', f),
    });
  });
  document.getElementById('btn-outcome')?.addEventListener('click', () => {
    if (_review) openOutcomeModal(_review.id);
  });
  document.getElementById('btn-iterate-procedural')?.addEventListener('click', () => _askIterate());
  document.getElementById('btn-iterate-ai')?.addEventListener('click', () => _openAiIteration());
  document.getElementById('btn-import-kit')?.addEventListener('click', () => {
    if (_review) openImportModal(_review.id, () => void _reload());
  });
  document.getElementById('btn-committee-feedback')?.addEventListener('click', () => {
    if (_review) openCommitteeFeedbackModal(_review.id, _review.recommendations, _reload);
  });
}

pages.backlogReview = definePage<'review/:id'>(async ({ id }) => {
  _readHash();
  _list.showAll = false;
  // `#review/0` = création, comme `#meeting/0` : on cadre DANS la page, sans
  // popup préalable. La revue n'existe en base qu'au « Créer la revue ».
  if (id === 0) {
    await renderReviewCreation();
    return;
  }
  setContent(`<div class="loading">${esc(t('common.loading'))}</div>`);
  try {
    _review = (await api.getBacklogReview(id)) as Review;
  } catch (e: unknown) {
    setContent(
      `<div class="error">${esc(e instanceof Error ? e.message : t('common.error'))}</div>`,
    );
    return;
  }
  // Le constat est déplié à la première ouverture, replié dès qu'une
  // recommandation a été arbitrée : on revient pour arbitrer, pas pour relire.
  _collapsed = _review.recommendations.some((r) => r.pile !== 'todo');
  _renderHeader(_review);
  _render();
  if (_review.run?.status === 'running') _poll();
});
